/**
 * «¿Qué me falta?» — pie del panel del plano: cuánto del modelo ya está en la hoja de presupuesto
 * (por piezas, área, largo, volumen) y qué falta, con un interruptor para verlo pintado en el plano.
 * Cerrado muestra solo el porcentaje; abierto, las barras y la lista. Clic en lo que falta = elegirlo.
 */
import { useState } from 'react';
import { Switch } from '@fluentui/react-components';
import { ChevronDownRegular, ChevronUpRegular } from '@fluentui/react-icons';
import {
  porcentajeDeAvance,
  type AvanceQueMeFalta,
  type ElementoQueFalta,
  type MedidaDeAvance,
} from '@supernova/shared';

const ETIQUETA: Record<MedidaDeAvance, { texto: string; unidad: string }> = {
  piezas: { texto: 'por piezas', unidad: 'piezas' },
  area: { texto: 'por área', unidad: 'm²' },
  longitud: { texto: 'por largo', unidad: 'm' },
  volumen: { texto: 'por volumen', unidad: 'm³' },
};

const fmt = (m: MedidaDeAvance, n: number) =>
  n.toLocaleString('es-MX', { maximumFractionDigits: m === 'piezas' ? 0 : 1 });

function detalle(f: ElementoQueFalta): string {
  if (f.piezas > 0) return f.piezasFaltan === f.piezas ? `${fmt('piezas', f.piezas)} pza` : `faltan ${fmt('piezas', f.piezasFaltan)} de ${fmt('piezas', f.piezas)} pza`;
  if (f.area > 0) return `${fmt('area', f.area)} m²`;
  if (f.longitud > 0) return `${fmt('longitud', f.longitud)} m`;
  return '';
}

const estiloFila = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '5px 0', border: 'none',
  borderBottom: '1px solid var(--border-subtle)', background: 'transparent',
  color: 'var(--text-primary)', font: 'inherit', cursor: 'pointer', textAlign: 'left',
} as const;
const estiloNombre = { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;
const estiloDetalle = { color: 'var(--text-secondary)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' } as const;

/** Agrupa por nombre (en IFC muchos tipos se llaman igual: «Elemento estructural», «Viga»). Mantiene el orden. */
function agrupar(faltan: ElementoQueFalta[]): [string, ElementoQueFalta[]][] {
  const m = new Map<string, ElementoQueFalta[]>();
  for (const f of faltan) (m.get(f.nombre) ?? m.set(f.nombre, []).get(f.nombre)!).push(f);
  return [...m.entries()];
}

export function QueMeFaltaPanel({
  avance,
  sinDecidir,
  verFalta,
  onVerFalta,
  onElegir,
}: {
  avance: AvanceQueMeFalta;
  /** Elementos de los que nadie decidió si se construyen: no cuentan. */
  sinDecidir: number;
  verFalta: boolean;
  onVerFalta: (v: boolean) => void;
  onElegir: (f: ElementoQueFalta) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [grupoAbierto, setGrupoAbierto] = useState<string | null>(null);
  const principal = avance.medidas[0];
  if (!principal) return null;
  const fila = (f: ElementoQueFalta, texto: string) => (
    <button key={`${f.tipo}:${f.clave}`} type="button" onClick={() => onElegir(f)} title="Elegirlo para asignarle concepto" style={estiloFila}>
      <span style={estiloNombre}>{texto}</span>
      <span style={estiloDetalle}>{detalle(f)}</span>
    </button>
  );
  const pct = Math.floor(porcentajeDeAvance(principal));
  const completo = avance.faltan.length === 0;
  return (
    <div
      style={{
        position: 'sticky', bottom: 0, marginTop: 'auto', padding: '8px 14px 10px',
        borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface)',
        fontSize: 12, color: 'var(--text-secondary)',
        flexShrink: 0, maxHeight: abierto ? '55vh' : undefined, overflowY: abierto ? 'auto' : undefined,
      }}
    >
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
        title={abierto ? 'Cerrar' : 'Ver qué te falta presupuestar'}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: 0, marginBottom: 5,
          border: 'none', background: 'transparent', color: 'inherit', font: 'inherit', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ flex: 1 }}>
          {completo ? (
            <b style={{ color: 'var(--text-primary)' }}>Todo lo que se construye ya está en tu hoja</b>
          ) : (
            <>
              Has presupuestado el <b style={{ color: 'var(--text-primary)' }}>{pct} %</b> del plano · {ETIQUETA[principal.medida].texto}
            </>
          )}
        </span>
        {abierto ? <ChevronDownRegular /> : <ChevronUpRegular />}
      </button>
      <div style={{ height: 5, borderRadius: 3, background: 'var(--bg-raised)', overflow: 'hidden' }}>
        <div style={{ width: `${porcentajeDeAvance(principal)}%`, height: '100%', background: 'var(--ok)', transition: 'width 200ms' }} />
      </div>

      {abierto && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          <Switch
            checked={verFalta}
            onChange={(_, d) => onVerFalta(d.checked)}
            label={
              <span>
                Ver en el plano: <b style={{ color: 'var(--bad)' }}>rojo</b> lo que falta, gris lo que ya está en tu hoja
              </span>
            }
          />
          {avance.medidas.map((m) => (
            <div key={m.medida}>
              <div style={{ display: 'flex' }}>
                <span style={{ flex: 1 }}>{ETIQUETA[m.medida].texto}</span>
                <span style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.floor(porcentajeDeAvance(m))} % · {fmt(m.medida, m.hecho)} de {fmt(m.medida, m.total)} {ETIQUETA[m.medida].unidad}
                </span>
              </div>
              <div style={{ height: 5, borderRadius: 3, background: 'var(--bg-raised)', overflow: 'hidden', marginTop: 3 }}>
                <div style={{ width: `${porcentajeDeAvance(m)}%`, height: '100%', background: 'var(--ok)' }} />
              </div>
            </div>
          ))}
          {!completo && (
            <>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600, marginTop: 4 }}>Lo que falta en tu hoja</div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {agrupar(avance.faltan).map(([nombre, els]) => {
                  if (els.length === 1) return fila(els[0]!, nombre);
                  const abiertoG = grupoAbierto === nombre;
                  const suma = els.reduce((t, f) => t + f.piezasFaltan, 0);
                  return (
                    <div key={`g:${nombre}`}>
                      <button
                        type="button"
                        onClick={() => setGrupoAbierto(abiertoG ? null : nombre)}
                        aria-expanded={abiertoG}
                        style={estiloFila}
                      >
                        <span style={estiloNombre}>
                          {nombre} <span style={{ color: 'var(--text-tertiary)' }}>· {els.length} tipos</span>
                        </span>
                        <span style={estiloDetalle}>{suma > 0 ? `${fmt('piezas', suma)} pza` : ''}</span>
                        {abiertoG ? <ChevronUpRegular /> : <ChevronDownRegular />}
                      </button>
                      {abiertoG && <div style={{ paddingLeft: 12 }}>{els.map((f) => fila(f, f.clave))}</div>}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {sinDecidir > 0 && (
            <div style={{ color: 'var(--text-tertiary)' }}>
              No cuentan {sinDecidir === 1 ? '1 elemento' : `${sinDecidir} elementos`} de los que falta decidir si se
              construyen. Lo que es solo dibujo tampoco cuenta.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
